import { IonIcon, useIonToast } from '@ionic/react';
import { cameraOutline } from 'ionicons/icons';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import {
  Camera,
  CameraDirection,
  CameraResultType,
  Photo,
} from '@capacitor/camera';
import { DEFAULT_AVATAR_URL } from '../../../model/models';
import { ParametersProps } from '../../wizard/WizardSteps';

export const PhotoParameters: React.FC<ParametersProps> = ({
  user,
  parameters,
  updateParameter,
  isSetLoading,
}) => {
  const [present] = useIonToast();
  const intl = useIntl();

  const takePicture = async () => {
    //see https://capacitorjs.com/docs/apis/camera

    const getPhoto = async () => {
      if (
        hasPermission.camera !== 'denied' ||
        hasPermission.photos !== 'denied'
      ) {
        const photo: Photo = await Camera.getPhoto({
          quality: 80,
          width: 300,
          height: 300,
          allowEditing: false,
          direction: CameraDirection.Front,
          resultType: CameraResultType.DataUrl,
          promptLabelHeader: intl.formatMessage({
            id: 'Update my profile picture',
          }),
          promptLabelCancel: intl.formatMessage({ id: 'Cancel' }),
          promptLabelPhoto: intl.formatMessage({ id: 'From Photos' }),
          promptLabelPicture: intl.formatMessage({ id: 'Take Picture' }),
        }).catch((err) => {
          return err;
        });

        if (photo.dataUrl) {
          resizeImage(photo.dataUrl, 300, 300).then((data) => {
            updateParameter('photo', data);
          });
        }
      }
    };

    var hasPermission = {
      camera: 'granted',
      photos: 'granted',
    };
    try {
      hasPermission = await Camera.checkPermissions();
    } catch (e) {
      // console.log(e);
      // some browser like firefox does not implement checkPermissions
    }
    if (
      hasPermission.camera === 'denied' ||
      hasPermission.photos === 'denied'
    ) {
      // const permission = await Camera.requestPermissions();
      present({
        color: 'dark',
        position: 'top',
        message: intl.formatMessage({
          id:
            hasPermission.camera === 'denied'
              ? 'Please allow access to the camera in the app settings on your phone'
              : 'Please allow access to the photo library in the app settings on your phone',
        }),
        duration: 3000,
        onDidDismiss: () => getPhoto(),
      });
    } else {
      getPhoto();
    }
  };

  const resizeImage = (dataUrl, maxWidth, maxHeight) => {
    return new Promise<string>((resolve, reject) => {
      // create an off-screen canvas
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (ctx === null) {
        resolve(dataUrl);
      } else {
        const img = new Image();
        img.onload = function () {
          let { width, height } = img;

          // Change the resizing logic
          if (width > height) {
            if (width > maxWidth) {
              height = height * (maxWidth / width);
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = width * (maxHeight / height);
              height = maxHeight;
            }
          }
          ctx.drawImage(img, 0, 0); // Or at whatever offset you like
          // set its dimension to target size
          canvas.width = width;
          canvas.height = height;

          // draw source image into the off-screen canvas:
          ctx?.drawImage(img, 0, 0, width, height);

          // encode image to data-uri with base64 version of compressed image
          resolve(canvas.toDataURL());
        };
        img.src = dataUrl;
      }
    });
  };

  useEffect(() => {
    isSetLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="w-32 mx-auto relative" onClick={() => takePicture()}>
      <img
        className="inline-block h-32 w-full object-cover rounded-full"
        src={parameters.photo || DEFAULT_AVATAR_URL}
        alt=""
      />
      <div className="absolute r-0 t-0 flex w-8 h-8 top-2 right-2 justify-center items-center border border-transparent rounded-full text-white bg-primary cursor-pointer">
        <IonIcon icon={cameraOutline} />
      </div>
    </div>
  );
};
